import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import './CommunityPageView.css';

const CommunityPageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(location.state?.page || null);
  const [loading, setLoading] = useState(!page);
  const [error, setError] = useState('');
  const [relatedPages, setRelatedPages] = useState([]);

  useEffect(() => {
    if (!page || page.slug !== slug || !page.content) {
      fetchPage();
    } else {
      fetchRelatedPages();
    }
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/pages/${slug}`);
      setPage(response.data);
      fetchRelatedPages();
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPages = async () => {
    try {
      if (page) {
        const response = await api.get(`/api/pages?related=${page._id}`);
        setRelatedPages(response.data);
      }
    } catch (err) {
      console.error('Error fetching related pages:', err);
    }
  };

  const handleBackClick = () => {
    navigate('/community-pages');
  };

  const handleRelatedPageClick = (relatedPage) => {
    navigate(`/community-pages/${relatedPage.slug}`, {
      state: { page: relatedPage }
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleBackClick} className="back-btn">
          ← Back to Community Pages
        </button>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="error-container">
        <h2>Page Not Found</h2>
        <p>The requested page could not be found.</p>
        <button onClick={handleBackClick} className="back-btn">
          ← Back to Community Pages
        </button>
      </div>
    );
  }

  return (
    <div className="page-view-container">
      {/* Navigation */}
      <div className="page-navigation">
        <button onClick={handleBackClick} className="back-btn">
          ← Back to Community Pages
        </button>
        
        <div className="page-breadcrumb">
          <span>Community Pages</span>
          <span className="breadcrumb-separator">›</span>
          <span>{page.title}</span>
        </div>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-meta-info">
          {page.isHomePage && <span className="home-badge">🏠 Home Page</span>}
          <span className="page-template">Template: {page.template || 'default'}</span>
          <span className="page-visibility">
            {page.visibility === 'public' ? '🌐 Public' : 
             page.visibility === 'members' ? '👥 Members Only' : '🔒 Admin Only'}
          </span>
        </div>
        
        <h1 className="page-title">{page.title}</h1>
        
        {page.excerpt && (
          <p className="page-excerpt" dangerouslySetInnerHTML={{ __html: page.excerpt }} />
        )}
        
        <div className="page-details">
          <span>Updated: {new Date(page.updatedAt).toLocaleDateString()}</span>
          {page.author && (
            <span>By: {page.author.name}</span>
          )}
        </div>
      </div>

      {/* Page Content */}
      <div className="page-content">
        <div className="content-body">
          {page && page.content ? (
            <div 
              className="page-text"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          ) : (
            <div className="no-content">
              <p>This page is under construction.</p>
              <p>Content is being prepared and will be available soon.</p>
            </div>
          )}
        </div>

        {/* Sidebar with related pages */}
        {relatedPages.length > 0 && (
          <div className="page-sidebar">
            <div className="related-pages">
              <h3>Related Pages</h3>
              <div className="related-pages-list">
                {relatedPages.map(relatedPage => (
                  <div 
                    key={relatedPage._id}
                    className="related-page-item"
                    onClick={() => handleRelatedPageClick(relatedPage)}
                  >
                    <h4>{relatedPage.title}</h4>
                    {relatedPage.excerpt && (
                      <p>{(() => { const t = document.createElement('div'); t.innerHTML = relatedPage.excerpt; return (t.textContent || t.innerText || '').substring(0, 100); })()}...</p>
                    )}
                    {!relatedPage.excerpt && relatedPage.content && (
                      <p>{(() => { const t = document.createElement('div'); t.innerHTML = relatedPage.content; return (t.textContent || t.innerText || '').substring(0, 100); })()}...</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPageView;